/**
 * Lot 320 — Bloc 2 : contexte investor multi-capacités.
 *
 * Fournit :
 *  - profiles : liste des profiles du user (PP + PM)
 *  - currentProfile : profile sélectionné pour filtrer l'UI
 *  - setCurrentProfile(profile_id) : change le profile actif (persisté localStorage)
 *  - reloadProfiles() : relit /api/investor/my-profiles
 *  - addProfile(payload) : crée un nouveau profile (société)
 *
 * LOT 537 v31 (Daniel 05/06/2026) — Robustification du cache :
 *  - Version key `wm.profileCache.v2` pour invalider tous les caches navigateurs
 *    existants en une seule fois (LOT 537 v17/v27/v28 → v31).
 *  - Namespace par user_id (`wm.profileCache.v2:<user_id>`) pour qu'un changement
 *    d'impersonation admin ne réutilise jamais le currentProfileId d'un autre user.
 *  - Purge PROACTIVE : si le profile_id stocké n'existe pas dans /my-profiles,
 *    il est immédiatement effacé du localStorage (pas seulement ignoré).
 *  - Filtre des profils TEST_* côté frontend (double sécurité) — le backend
 *    LOT 537 v27 les filtre déjà mais on garantit que rien ne fuit au cas où.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// LOT 537 v31 — Version key. Incrémenter quand on veut forcer un reset du cache
// chez tous les navigateurs en production. Migrate legacy key au passage.
const CACHE_VERSION = 'v2';
const STORAGE_PREFIX = `wm.profileCache.${CACHE_VERSION}`;
const LEGACY_STORAGE_KEY = 'investor_current_profile_id';

const storageKeyFor = (userId) => `${STORAGE_PREFIX}:${userId || 'anon'}:currentProfileId`;

// LOT 537 v31 — Détection des profils de test côté frontend (double sécurité).
const TEST_PROFILE_REGEX = /^TEST_(DEBUG_|PM_|FORK_|LOT)/i;
const isTestProfile = (p) => {
  if (!p) return false;
  if (p.hidden === true) return true;
  if (p.is_test_profile === true) return true;
  const name = p.display_name || p.entity_name || '';
  return TEST_PROFILE_REGEX.test(String(name).trim());
};

const InvestorProfileContext = createContext(null);

export function InvestorProfileProvider({ children }) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [currentProfileId, setCurrentProfileId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadProfiles = useCallback(async (opts = {}) => {
    if (!user) return;
    // Lot 346 — Empêcher la fuite hors scope : seuls les investisseurs ont des profils PP/PM.
    if (user.role && user.role !== 'investor') {
      setProfiles([]);
      return;
    }
    // RETOUR ÉMILIE 04/09 (cas 2/19) — mode silencieux pour les
    // re-synchronisations d'arrière-plan (pas de flash de loading).
    if (!opts.silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/api/investor/my-profiles`, { withCredentials: true });
      const rawList = res.data?.profiles || [];

      // LOT 537 v31 — Filtre frontend : on retire les profils TEST_* de la liste
      // visible dans le switcher. Le backend LOT 537 v27 les filtre déjà mais
      // on garantit une double sécurité en cas de régression backend.
      const list = rawList.filter((p) => !isTestProfile(p));
      setProfiles(list);

      // LOT 537 v31 — Migration : si une ancienne clé legacy existe encore,
      // on la nettoie pour éviter qu'elle pollue à nouveau le contexte.
      try {
        if (localStorage.getItem(LEGACY_STORAGE_KEY)) {
          localStorage.removeItem(LEGACY_STORAGE_KEY);
        }
      } catch { /* localStorage indispo (mode privé) — non bloquant */ }

      // LOT 537 v31 — Résolution du current profile avec namespace par user_id.
      const key = storageKeyFor(user.id);
      let stored = null;
      try { stored = localStorage.getItem(key); } catch { stored = null; }

      const storedExists = stored && list.find((p) => p.id === stored);

      if (storedExists) {
        setCurrentProfileId(stored);
      } else {
        // PURGE PROACTIVE : si la clé existe mais ne matche plus, on l'efface
        // immédiatement (cas Marc : ancien TEST_DEBUG_PM dans localStorage).
        if (stored) {
          try { localStorage.removeItem(key); } catch { /* ignore */ }
        }
        const primary = list.find((p) => p.is_primary) || list[0];
        if (primary) {
          setCurrentProfileId(primary.id);
          try { localStorage.setItem(key, primary.id); } catch { /* ignore */ }
        } else {
          setCurrentProfileId(null);
        }
      }
    } catch (e) {
      setProfiles([]);
    }
    setLoading(false);
  }, [user]);

  // RETOUR ÉMILIE 04/09 (relevé iteration_337) — déduplication des fetchs au
  // mount : `user` change plusieurs fois d'identité d'objet au boot
  // (login → /auth/me → refresh) et déclenchait 3 GET /my-profiles.
  // On ne recharge automatiquement qu'au changement de user.id ; les
  // rechargements explicites (reloadProfiles, mutations, re-sync focus)
  // restent inchangés.
  const lastAutoLoadUserIdRef = useRef(null);
  useEffect(() => {
    const uid = user?.id || null;
    if (uid === lastAutoLoadUserIdRef.current) return;
    lastAutoLoadUserIdRef.current = uid;
    loadProfiles();
  }, [user?.id, loadProfiles]);

  // RETOUR ÉMILIE 04/09 (cas 2/19) — CAUSE RACINE « stale UI » : les profils
  // n'étaient chargés qu'au mount ; toute mutation faite HORS du contexte
  // (fiche admin, autre onglet, impersonation) laissait le switcher sur
  // l'ancienne dénomination jusqu'au rechargement complet de la page.
  // Mécanisme générique : re-synchronisation SILENCIEUSE au retour de focus
  // ou de visibilité de l'onglet (throttle 15 s pour ménager l'API).
  useEffect(() => {
    if (!user || (user.role && user.role !== 'investor')) return undefined;
    let lastSync = Date.now();
    const maybeResync = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (Date.now() - lastSync < 15000) return;
      lastSync = Date.now();
      loadProfiles({ silent: true });
    };
    window.addEventListener('focus', maybeResync);
    document.addEventListener('visibilitychange', maybeResync);
    return () => {
      window.removeEventListener('focus', maybeResync);
      document.removeEventListener('visibilitychange', maybeResync);
    };
  }, [user, loadProfiles]);

  // LOT 537 v31 — Invalidation des caches au changement d'utilisateur
  // (logout/login OU impersonation admin). On purge toutes les clés de
  // profile cache des autres users pour éviter qu'un admin retrouve par
  // erreur un currentProfileId périmé d'une session précédente.
  useEffect(() => {
    if (!user?.id) return;
    try {
      const keys = Object.keys(localStorage);
      const myKey = storageKeyFor(user.id);
      keys.forEach((k) => {
        if (k.startsWith(`${STORAGE_PREFIX}:`) && k !== myKey) {
          // On garde uniquement la clé du user actif courant
          localStorage.removeItem(k);
        }
      });
    } catch { /* ignore */ }
  }, [user?.id]);

  const selectProfile = (profileId) => {
    // LOT 537 v60 (Daniel 07/06/2026) — Support de la vue globale explicite :
    // `selectProfile(null)` désélectionne tout profil et place le user en
    // mode "Vue globale (tous mes profils)". Le localStorage est NETTOYÉ
    // pour éviter de stocker la string "null" qui serait réinterprétée
    // (regression v31).
    setCurrentProfileId(profileId);
    if (user?.id) {
      try {
        if (profileId === null || profileId === undefined) {
          localStorage.removeItem(storageKeyFor(user.id));
        } else {
          localStorage.setItem(storageKeyFor(user.id), profileId);
        }
      } catch { /* ignore */ }
    }
  };

  // LOT 537 v60 — Helper exposé pour l'UI : true si l'utilisateur a explicitement
  // sélectionné "Vue globale" (currentProfileId == null) ET a plusieurs profils.
  // Permet aux composants de décider entre `cross_profile=true` (vue globale)
  // et `?profile_id=X` (vue scopée). Cas Marc/Quentin/Richard (1 seul profil)
  // restent en cross_profile par défaut, conservant l'anti-régression v49.
  const isGlobalView = currentProfileId == null && profiles.length > 1;

  const addProfile = async (payload) => {
    const res = await axios.post(`${API}/api/investor/my-profiles`, payload, { withCredentials: true });
    await loadProfiles();
    return res.data;
  };

  const updateProfile = async (profileId, payload) => {
    const res = await axios.put(`${API}/api/investor/my-profiles/${profileId}`, payload, { withCredentials: true });
    await loadProfiles();
    return res.data;
  };

  const currentProfile = profiles.find((p) => p.id === currentProfileId) || null;

  const value = {
    profiles,
    currentProfile,
    currentProfileId,
    isGlobalView,
    selectProfile,
    addProfile,
    updateProfile,
    reloadProfiles: loadProfiles,
    loading,
  };

  return (
    <InvestorProfileContext.Provider value={value}>
      {children}
    </InvestorProfileContext.Provider>
  );
}

export function useInvestorProfile() {
  const ctx = useContext(InvestorProfileContext);
  return ctx || {
    profiles: [], currentProfile: null, currentProfileId: null, isGlobalView: false,
    selectProfile: () => {}, addProfile: () => Promise.resolve(null),
    updateProfile: () => Promise.resolve(null),
    reloadProfiles: () => {}, loading: false,
  };
}
